import React, { useEffect, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { Slider } from "@mui/material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Modal,
  Skeleton,
  Snackbar,
  Switch,
  TextField,
  Typography,
  Popover,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";
import { generate_filter_condition } from "../utiles/filter";
import CreatedByCheckbox from "../components/createdbycheckbox";
import { useSearchParams } from "react-router-dom";
import DynamicDataGrid from "../components/dynamicDataGrid";
import CandidateDetails from "../components/candidateDetails";

const SearchCandidate = ({ searchPayload }) => {
  const PermissionStatus = useSelector((state) => state.user.permissions);
  const addCandidatePermission = PermissionStatus?.["100"];
  const [searchParams, setSearchParams] = useSearchParams();
  const debounceTimer = useRef(null);

  // ── Table data ──
  const [searchdata, setsearchdata] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [queryOptions, setQueryOptions] = useState({});
  const lastPayload = useRef(searchPayload);

  // ── Search bar (table filter) ──
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("ftSearch") || "",
  );

  // ── Candidate details ──
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [isApplicanOpen, setApplicanOpen] = useState(false);

  // ── Add to Job ──
  const [addToJobOpen, setAddToJobOpen] = useState(false);
  const [addToJobCandidateSelection, setAddToJobCandidateSelection] = useState(
    [],
  );
  const [jobOrder, setJobOrder] = useState("");
  const [inputValueJobOrder, setInputValueJobOrder] = useState("");
  const [jobOrderOptions, setJobOrderOptions] = useState([]);
  const [joborderIds, setJoborderIds] = useState();
  const [bulkdata, setbulkdata] = useState([]);
  const [adddisbale, setadddisable] = useState(false);
  const [shimmer, setshimmer] = useState(false);
  const [warning, setwarning] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // ── Filter popover ──
  const [anchorEl, setAnchorEl] = useState(null);
  const isPopoverOpen = Boolean(anchorEl);
  const [candidateList, setcandidateList] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [username, setUsername] = useState("Select User Name");
  const [preferredLocation, setpreferedLocation] = useState([]);
  const [preferredArr, setPreferredArr] = useState([]);
  const [minsalary, setminsalary] = useState("");
  const [maxsalary, setmaxsalary] = useState("");
  const [experienceRange, setExperienceRange] = useState([0, 50]);
  const [isBooleanSearch, setIsBooleanSearch] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [createdby, setcreatedby] = useState("");

  // ── Initial fetch from FindTalent payload ──
  const doSearch = (payload, page) => {
    setShowLoader(true);
    setsearchdata([]);
    const searchVal =
      new URLSearchParams(window.location.search).get("ftSearch") || "";
    const url = searchVal
      ? `find-talent-new/${page}?search=${encodeURIComponent(searchVal)}`
      : `find-talent-new/${page}`;
    axios
      .post(url, payload, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      })
      .then((res) => {
        setsearchdata(res.data.message.search_result);
        setTotalCount(res.data.message.total_records);
        setShowLoader(false);
      })
      .catch((err) => {
        console.error(err);
        setShowLoader(false);
      });
  };

  useEffect(() => {
    if (searchPayload) {
      lastPayload.current = searchPayload;
      setPaginationModel({ page: 0, pageSize: 10 });
      setPageNumber(1);
      doSearch(searchPayload, 1);
    } else {
      setsearchdata([]);
      setTotalCount(0);
      lastPayload.current = null;
    }
  }, [searchPayload]);

  useEffect(() => {
    if (!lastPayload.current) return;
    const timer = setTimeout(() => {
      doSearch(lastPayload.current, 1);
    }, 0);
    return () => clearTimeout(timer);
  }, [searchParams]);

  // ── Fetch supporting data ──
  useEffect(() => {
    const user_id = localStorage.getItem("user_id");
    axios
      .get(`active-users/${user_id}`, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      })
      .then((res) => {
        setcandidateList(res.data.message);
      })
      .catch(console.error);

    axios
      .get("all-jobs", {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      })
      .then((res) => setJobOrderOptions(res?.data?.message || []))
      .catch(console.error);
  }, []);

  // ── Pagination ──
  const handlePaginationChange = (model) => {
    setPaginationModel(model);
    const newPage = model.page + 1;
    setPageNumber(newPage);
    if (!lastPayload.current) return;
    doSearch({ ...lastPayload.current, pageSize: model.pageSize }, newPage);
  };

  // ── Sort ──
  const handleSortModelChange = (sortModel) => {
    setQueryOptions({ sortModel: [...sortModel] });
    if (!lastPayload.current) return;
    doSearch(
      {
        ...lastPayload.current,
        sortField: sortModel[0]?.field || "",
        sortBy: sortModel[0]?.sort || "",
      },
      pageNumber,
    );
  };

  // ── Table search filter ──
  const filteredRows = searchTerm.trim()
    ? searchdata.filter((row) =>
        Object.values(row).some((val) =>
          String(val ?? "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        ),
      )
    : searchdata;

  // ── Row selection (Add to Job) ──
  const handleRowSelectionChange = (newSelection) => {
    const deselected = addToJobCandidateSelection.filter(
      (id) => !newSelection.includes(id),
    );
    const current = addToJobCandidateSelection.length - deselected.length;
    const validNew = newSelection.filter(
      (id) => !addToJobCandidateSelection.includes(id),
    );
    const limited = validNew.slice(0, 10 - current);
    if (validNew.length > limited.length) setwarning(true);
    else setwarning(false);
    setAddToJobCandidateSelection([
      ...addToJobCandidateSelection.filter((id) => !deselected.includes(id)),
      ...limited,
    ]);
  };

  // ── Add to Job ──
  const handleAddCandidates = () => {
    axios
      .post(
        "bulk-add/job",
        {
          candidate_ids: addToJobCandidateSelection,
          job_id: joborderIds || "",
          quesAnsArray: [],
          isShortlisted: 0,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      )
      .then((res) => {
        if (res.status === 200) {
          setAddToJobCandidateSelection([]);
          setbulkdata(res.data.message.details);
          if (res.data.message.failure_candidates >= 1) setadddisable(true);
          setSnackbarMessage("Candidate Added Successfully");
          setOpenSnackbar(true);
          if (res.data.message.failure_candidates === 0) setAddToJobOpen(false);
        }
      })
      .catch((err) => {
        setSnackbarMessage(
          err?.response?.data?.error || "Something went wrong",
        );
        setOpenSnackbar(true);
        setAddToJobOpen(false);
      });
  };

  // ── Filter popover ──
  const handleSelectionData = (event, value, reason) => {
    setSelectedOptions(value);
    const usernames = value.map((item) => item.name);
    if (usernames.length > 0)
      setcreatedby(
        generate_filter_condition("entered_by", "isAnyOf", usernames),
      );
    else setcreatedby("");
    if (reason === "clear") {
      setcreatedby("");
      setSelectedOptions([]);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        height: "100%",
      }}
    >
      {/* ── Top bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "16px", color: "#1a1a2e" }}
        >
          Results{" "}
          {totalCount > 0 && (
            <span style={{ fontWeight: 400, fontSize: "13px", color: "#888" }}>
              ({totalCount})
            </span>
          )}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Search bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #d0d5dd",
              borderRadius: "8px",
              padding: "4px 10px",
              backgroundColor: "#fff",
              gap: 1,
              height: "32px",
              width: "200px",
            }}
          >
            <SearchIcon sx={{ fontSize: "16px", color: "#aaa" }} />
            <input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                if (debounceTimer.current) clearTimeout(debounceTimer.current);
                debounceTimer.current = setTimeout(() => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (val) next.set("ftSearch", val);
                    else next.delete("ftSearch");
                    return next;
                  });
                  setPaginationModel({ page: 0, pageSize: 10 });
                  setPageNumber(1);
                }, 500);
              }}
              style={{
                border: "none",
                outline: "none",
                fontSize: "13px",
                color: "#333",
                width: "100%",
                backgroundColor: "transparent",
              }}
            />
          </Box>

          {/* Add to Job */}
          {addCandidatePermission?.add === 1 && (
            <Button
              variant="contained"
              disabled={addToJobCandidateSelection.length < 1 || warning}
              onClick={() => {
                setAddToJobOpen(true);
                // setadddisable(false);
                setbulkdata([]);
              }}
              sx={{
                height: "32px",
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "#455a64",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#3F95EA", boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: "#ccc", color: "#fff" },
              }}
            >
              + ADD TO JOB
            </Button>
          )}

          {/* Filter button */}
          {/* <Button
            variant="outlined"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            startIcon={<FilterAltOutlinedIcon />}
            sx={{
              height: "32px",
              fontSize: "13px",
              textTransform: "none",
              borderRadius: "8px",
              borderColor: "#d0d5dd",
              color: "#455a64",
              "&:hover": { borderColor: "#455a64", backgroundColor: "#f5f5f5" },
            }}
          >
            Filter
          </Button> */}
        </Box>
      </Box>

      {/* ── Warning ── */}
      {warning && (
        <Typography sx={{ fontSize: "12px", color: "#d32f2f" }}>
          You cannot select more than 10 candidates.
        </Typography>
      )}

      {/* ── Table ── */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DynamicDataGrid
          rows={searchdata}
          loading={showLoader}
          showCheckbox={addCandidatePermission?.add === 1}
          totalRows={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          onRowSelection={(row) => {
            setSelectedRowData(row);
            setApplicanOpen(true);
          }}
          onRowSelectionModelChange={handleRowSelectionChange}
          rowSelectionModel={addToJobCandidateSelection}
          sortModel={queryOptions.sortModel}
          onSortModelChange={handleSortModelChange}
          hideFooter={false}
          noRowsText="No Candidates Found"
          paginationMode="server"
          sortingMode="server"
        />
      </Box>

      {/* ── Add to Job Modal ── */}
      <Modal
        open={addToJobOpen}
        onClose={() => {
          setAddToJobOpen(false);
          setJobOrder("");
          setbulkdata([]);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 500, p: 2 }}>
            Add to Job
          </Typography>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" mb={1}>
              Select Job
            </Typography>
            <Autocomplete
              value={jobOrder}
              onChange={(e, val) => setJobOrder(val)}
              inputValue={inputValueJobOrder}
              onInputChange={(e, val) => {
                setJoborderIds(val.split(",").pop().trim());
                setInputValueJobOrder(val);
              }}
              options={jobOrderOptions.map(
                (opt) => `${opt.title}, ${opt.joborder_id}`,
              )}
              sx={{ mt: 1 }}
              renderInput={(params) => (
                <TextField {...params} label="Job" size="small" />
              )}
            />
            {bulkdata.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {bulkdata.length} candidate(s) already in this job. Remaining
                  added successfully.
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {bulkdata.map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        backgroundColor: "#FDEDED",
                        p: 1.5,
                        borderRadius: 2,
                        width: 200,
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {item.first_name} {item.last_name}
                      </Typography>
                      <Typography variant="caption">{item.email1}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <Divider />
          <Box
            sx={{ p: 1.5, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              className="cancel-btn"
              variant="text"
              size="small"
              onClick={() => {
                setAddToJobOpen(false);
                setJobOrder("");
                setbulkdata([]);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={adddisbale || !jobOrder}
              onClick={handleAddCandidates}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Candidate Details ── */}
      <CandidateDetails
        issearchnavbar={true}
        isOpen={isApplicanOpen}
        setApplicanOpen={setApplicanOpen}
        onClose={() => setApplicanOpen(false)}
        selectedRowData={selectedRowData}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          severity={
            snackbarMessage === "Candidate Added Successfully"
              ? "success"
              : "error"
          }
          onClose={() => setOpenSnackbar(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SearchCandidate;
